import React from 'react';
import { Clock } from 'lucide-react';

export type ActivityColor = 'green' | 'yellow' | 'orange' | 'red' | 'neutral';

interface ActivityCellProps {
  lastActivityDate: string | null;
  lastActivityTimestamp: string | null; // Full ISO timestamp for hours calculation
  daysSince: number;
  color: ActivityColor;
}

/**
 * ActivityCell - Engagement indicator for Coach Dashboard
 *
 * Color Thresholds (Column 10):
 * - 🟢 GREEN: 0-2 days (Active)
 * - 🟡 YELLOW: 3-5 days (Attention)
 * - 🟠 ORANGE: 6-7 days (Warning)
 * - 🔴 RED: 8+ days (Critical)
 * - ⚪ NEUTRAL: Unknown/No data
 *
 * Display Format:
 * - < 24 hours: "8 hours ago\n10/13/2025"
 * - ≥ 24 hours: "3 days ago\n10/10/2025"
 */
export const ActivityCell: React.FC<ActivityCellProps> = ({
  lastActivityDate,
  lastActivityTimestamp,
  daysSince,
  color
}) => {
  const getColorStyles = () => {
    switch (color) {
      case 'green':
        return {
          bg: '#22c55e',      // Vibrant green
          text: '#ffffff',    // White text
          border: '#16a34a',  // Darker green border
          icon: '#ffffff'     // White icon
        };
      case 'yellow':
        return {
          bg: '#fbbf24',      // Vibrant amber
          text: '#78350f',    // Dark brown text
          border: '#f59e0b',  // Darker amber border
          icon: '#78350f'     // Dark brown icon
        };
      case 'orange':
        return {
          bg: '#f97316',      // Vibrant orange
          text: '#ffffff',    // White text
          border: '#ea580c',  // Darker orange border
          icon: '#ffffff'     // White icon
        };
      case 'red':
        return {
          bg: '#ef4444',      // Vibrant red
          text: '#ffffff',    // White text
          border: '#dc2626',  // Darker red border
          icon: '#ffffff'     // White icon
        };
      case 'neutral':
        return {
          bg: '#64748b',      // Slate gray
          text: '#ffffff',    // White text
          border: '#475569',  // Darker slate border
          icon: '#ffffff'     // White icon
        };
      default:
        return {
          bg: '#64748b',
          text: '#ffffff',
          border: '#475569',
          icon: '#ffffff'
        };
    }
  };

  const formatActivityText = () => {
    if (!lastActivityDate || daysSince < 0) {
      return 'Unknown';
    }

    // < 24 hours: show hours (use timestamp for precision)
    if (daysSince === 0 && lastActivityTimestamp) {
      const lastActivity = new Date(lastActivityTimestamp);
      const now = new Date();
      const hoursSince = Math.floor((now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60));

      if (hoursSince === 0) {
        return 'Just now';
      } else if (hoursSince === 1) {
        return '1 hour ago';
      } else {
        return `${hoursSince} hours ago`;
      }
    }

    // 1-13 days: show days
    if (daysSince >= 1 && daysSince < 14) {
      return daysSince === 1 ? 'Yesterday' : `${daysSince} days ago`;
    }

    // 14-83 days (up to 12 weeks): show weeks
    if (daysSince >= 14 && daysSince < 84) {
      const weeks = Math.floor(daysSince / 7);
      return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
    }

    // 84+ days (12+ weeks): show months
    if (daysSince >= 84) {
      const months = Math.floor(daysSince / 30); // Approximate month as 30 days
      return months === 1 ? '1 month ago' : `${months} months ago`;
    }

    return 'Unknown';
  };

  const formatDate = () => {
    if (!lastActivityDate) {
      return 'No activity';
    }

    try {
      // Parse YYYY-MM-DD as local date to avoid timezone drift
      const [year, month, day] = lastActivityDate.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed

      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const colors = getColorStyles();
  const activityText = formatActivityText();
  const dateText = formatDate();

  return (
    <>
      <div
        className="activity-cell"
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          borderColor: colors.border
        }}
      >
        <div className="activity-icon" style={{ color: colors.icon }}>
          <Clock size={16} />
        </div>
        <div className="activity-content">
          <div className="activity-text">{activityText}</div>
          <div className="activity-date">{dateText}</div>
        </div>
      </div>

      <style>{`
        .activity-cell {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 6px;
          border: 1px solid;
          font-size: 13px;
          min-width: 140px;
          transition: all 0.2s ease;
        }

        .activity-cell:hover {
          opacity: 0.9;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
        }

        .activity-icon {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .activity-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
          flex: 1;
        }

        .activity-text {
          font-weight: 600;
          font-size: 13px;
          line-height: 1.2;
        }

        .activity-date {
          font-size: 11px;
          font-weight: 500;
          opacity: 0.8;
          line-height: 1.2;
        }
      `}</style>
    </>
  );
};
